import React from 'react';
import { render, screen, fireEvent, waitFor } from '@testing-library/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import ActivitiesTab from '../ActivitiesTab';
import Api from '../API';

jest.mock('../API');
const mockApi = Api as jest.Mocked<typeof Api>;

jest.mock('../AddRecord', () => {
  return function MockAddRecord({
    isOpen,
    handleClose,
    addRecord
  }: {
    isOpen: boolean;
    handleClose: () => void;
    addRecord: (params: { subject: string; body: string }) => void;
  }) {
    return isOpen ? (
      <div data-testid="add-record-modal">
        <button onClick={() => addRecord({ subject: 'Test', body: 'Body' })}>Save</button>
        <button onClick={handleClose}>Close</button>
      </div>
    ) : null;
  };
});

const createTestQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        retry: false
      }
    }
  });

const theme = createTheme();

const renderWithProviders = (component: React.ReactElement) => {
  const queryClient = createTestQueryClient();
  return render(
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>{component}</ThemeProvider>
    </QueryClientProvider>
  );
};

const mockActivities = {
  activities: [
    {
      id: 1,
      timestamp: new Date().toISOString(),
      actor: {
        id: 1,
        name: 'John Doe',
        email: 'john@example.com',
        firstname: 'John',
        lastname: 'Doe',
        system: false
      },
      activity_object: { id: 1, type: 'Candidate' },
      description: 'Test activity',
      verb: 'sent',
      indirect_object: { id: 1, type: 'Email', subject: 'Test Subject', body: 'Test Body' },
      activity_html: 'sent an email',
      email_status: 'delivered'
    },
    {
      id: 2,
      timestamp: new Date().toISOString(),
      actor: {
        id: 2,
        name: 'System',
        email: 'system@example.com',
        firstname: 'System',
        lastname: '',
        system: true
      },
      activity_object: { id: 1, type: 'Candidate' },
      description: 'System activity',
      verb: 'Updated the note',
      indirect_object: { id: 1, type: 'Note', subject: 'Note Subject', body: 'Note Body' },
      activity_html: 'updated a note',
      email_status: null
    }
  ]
};

describe('ActivitiesTab', () => {
  const mockDispatch = jest.fn();

  beforeEach(() => {
    jest.clearAllMocks();
    mockApi.getCandidateActivities.mockResolvedValue({ res: mockActivities });
    mockApi.addCandidateNote.mockResolvedValue({ res: { success: 'Note added' }, resHead: {} });
  });

  it('renders add record button when candidateId is provided', () => {
    const { container } = renderWithProviders(
      <ActivitiesTab candidateId={1} dispatch={mockDispatch} />
    );

    const button = container.querySelector('#add-record-button');
    expect(button).toBeInTheDocument();
    expect(screen.getByText('Add record')).toBeInTheDocument();
  });

  it('does not render add record button when candidateId is not provided', () => {
    const { container } = renderWithProviders(<ActivitiesTab dispatch={mockDispatch} />);

    expect(container.querySelector('#add-record-button')).not.toBeInTheDocument();
  });

  it('opens add record modal when button is clicked', () => {
    const { container } = renderWithProviders(
      <ActivitiesTab candidateId={1} dispatch={mockDispatch} />
    );

    const addRecordButton = container.querySelector('#add-record-button') as HTMLButtonElement;
    fireEvent.click(addRecordButton);

    expect(screen.getByTestId('add-record-modal')).toBeInTheDocument();
  });

  it('displays loading skeletons when loading activities', () => {
    mockApi.getCandidateActivities.mockImplementation(() => new Promise(() => {}));

    const { container } = renderWithProviders(
      <ActivitiesTab candidateId={1} dispatch={mockDispatch} />
    );

    const skeletons = container.querySelectorAll('.MuiSkeleton-root');
    expect(skeletons.length).toBeGreaterThan(0);
  });

  it('displays activities when loaded', async () => {
    renderWithProviders(<ActivitiesTab candidateId={1} dispatch={mockDispatch} />);

    await waitFor(() => {
      expect(screen.getByText(/sent an email/i)).toBeInTheDocument();
    });
  });

  it('displays activity actor name', async () => {
    renderWithProviders(<ActivitiesTab candidateId={1} dispatch={mockDispatch} />);

    await waitFor(() => {
      expect(screen.getByText(/John Doe/i)).toBeInTheDocument();
    });
  });

  it('displays email status when present', async () => {
    renderWithProviders(<ActivitiesTab candidateId={1} dispatch={mockDispatch} />);

    await waitFor(() => {
      expect(screen.getByText('delivered')).toBeInTheDocument();
    });
  });

  it('shows expand/collapse button for activities with details', async () => {
    renderWithProviders(<ActivitiesTab candidateId={1} dispatch={mockDispatch} />);

    await waitFor(() => {
      expect(screen.getByText('Show')).toBeInTheDocument();
    });
  });

  it('expands activity details when show button is clicked', async () => {
    renderWithProviders(<ActivitiesTab candidateId={1} dispatch={mockDispatch} />);

    await waitFor(() => {
      const showButton = screen.getAllByText('Show')[0];
      fireEvent.click(showButton);
    });

    await waitFor(() => {
      expect(screen.getByText('Test Body')).toBeInTheDocument();
    });
  });

  it('calls API with correct candidateId', async () => {
    renderWithProviders(<ActivitiesTab candidateId={123} dispatch={mockDispatch} />);

    await waitFor(() => {
      expect(mockApi.getCandidateActivities).toHaveBeenCalledWith(123);
    });
  });

  it('handles API error when fetching activities', async () => {
    const consoleError = jest.spyOn(console, 'error').mockImplementation(() => undefined);
    mockApi.getCandidateActivities.mockRejectedValue(new Error('API Error'));

    renderWithProviders(<ActivitiesTab candidateId={1} dispatch={mockDispatch} />);

    await waitFor(() => {
      expect(mockDispatch).toHaveBeenCalledWith({
        type: 'SET_SNACKBAR',
        payload: {
          message: 'There was an error getting candidate activities, Error: API Error',
          state: 'error'
        }
      });
    });

    consoleError.mockRestore();
  });

  it('calls addCandidateNote when record is added', async () => {
    const { container } = renderWithProviders(
      <ActivitiesTab candidateId={1} dispatch={mockDispatch} />
    );

    const addRecordButton = container.querySelector('#add-record-button') as HTMLButtonElement;
    fireEvent.click(addRecordButton);

    await waitFor(() => {
      const saveButton = screen.getByText('Save');
      fireEvent.click(saveButton);
    });

    await waitFor(() => {
      expect(mockApi.addCandidateNote).toHaveBeenCalledWith(1, {
        subject: 'Test',
        body: 'Body'
      });
    });
  });

  it('handles addCandidateNote success', async () => {
    const { container } = renderWithProviders(
      <ActivitiesTab candidateId={1} dispatch={mockDispatch} />
    );

    const addRecordButton = container.querySelector('#add-record-button') as HTMLButtonElement;
    fireEvent.click(addRecordButton);

    await waitFor(() => {
      const saveButton = screen.getByText('Save');
      fireEvent.click(saveButton);
    });

    await waitFor(() => {
      expect(mockDispatch).toHaveBeenCalledWith({
        type: 'SET_SNACKBAR',
        payload: { message: 'Note added', state: 'success' }
      });
    });
  });

  it('handles addCandidateNote error', async () => {
    mockApi.addCandidateNote.mockRejectedValue({ error: 'Error message' });

    const { container } = renderWithProviders(
      <ActivitiesTab candidateId={1} dispatch={mockDispatch} />
    );

    const addRecordButton = container.querySelector('#add-record-button') as HTMLButtonElement;
    fireEvent.click(addRecordButton);

    await waitFor(() => {
      const saveButton = screen.getByText('Save');
      fireEvent.click(saveButton);
    });

    await waitFor(() => {
      expect(mockDispatch).toHaveBeenCalledWith({
        type: 'SET_SNACKBAR',
        payload: {
          message: 'There was an error in adding permanent record, [object Object]',
          state: 'error'
        }
      });
    });
  });
});
